import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Image as ImageIcon, Upload, Plus, Trash2, MessageCircle, Settings as SettingsIcon, Sparkles } from 'lucide-react';
import axios from 'axios';

const API = window.location.origin + '/api';

const blankPromo = () => ({
  id: `promo-${Date.now()}`,
  title: '',
  subtitle: '',
  cta_label: 'Shop Now',
  cta_link: '/',
  gradient_from: '#0A1F44',
  gradient_to: '#1E40AF',
  accent: '#C8FF00',
  sponsored_by: '',
  active: true,
  order: 99,
});

export default function AdminSettings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    store_name: 'StoreRuns',
    whatsapp_number: '',
    hero_cover_image_url: '',
    promos: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    axios.get(`${API}/settings`)
      .then(r => setSettings(s => ({ ...s, ...(r.data || {}), promos: r.data?.promos || [] })))
      .catch(err => console.error('Failed to load settings:', err.message))
      .finally(() => setLoading(false));
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setMsg('');
    try {
      await axios.put(`${API}/admin/settings`, settings, { withCredentials: true });
      setMsg('Settings saved successfully.');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  }, [settings]);

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/upload`, fd, { withCredentials: true });
      const url = r.data?.url;
      if (url) {
        setSettings(s => ({ ...s, hero_cover_image_url: url }));
        setMsg('Cover image uploaded. Click Save to apply.');
      }
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Upload failed');
    }
    setUploadingCover(false);
  };

  const updatePromo = (idx, patch) => {
    setSettings(s => ({
      ...s,
      promos: s.promos.map((p, i) => i === idx ? { ...p, ...patch } : p),
    }));
  };

  const removePromo = (idx) => {
    if (!window.confirm('Remove this promo slide?')) return;
    setSettings(s => ({ ...s, promos: s.promos.filter((_, i) => i !== idx) }));
  };

  const addPromo = () => {
    setSettings(s => ({ ...s, promos: [...s.promos, blankPromo()] }));
  };

  if (loading) return <div className="loading-screen">Loading settings…</div>;

  return (
    <div className="admin-page" data-testid="admin-settings-page">
      <button className="back-btn" onClick={() => navigate('/admin')} data-testid="back-btn">
        <ArrowLeft /> Admin Dashboard
      </button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 8 }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <SettingsIcon size={22} /> Store Settings
        </h2>
        <button className="btn-lime" onClick={save} disabled={saving} data-testid="save-all-btn"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: 'auto', padding: '10px 22px' }}>
          <Save size={16} /> {saving ? 'Saving…' : 'Save All'}
        </button>
      </div>
      {msg && (
        <div data-testid="settings-msg" style={{
          padding: '10px 14px', borderRadius: 8, marginBottom: 14,
          background: msg.includes('failed') || msg.includes('Save failed') ? '#FEE2E2' : '#ECFDF5',
          color: msg.includes('failed') || msg.includes('Save failed') ? '#991B1B' : '#065F46',
          fontSize: '0.86rem', fontWeight: 600,
        }}>{msg}</div>
      )}

      {/* General settings */}
      <section className="settings-section" data-testid="general-section">
        <h3>General</h3>
        <div className="form-group">
          <label>Store Name</label>
          <input value={settings.store_name || ''}
            onChange={e => setSettings({ ...settings, store_name: e.target.value })}
            data-testid="setting-store-name" />
        </div>
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <MessageCircle size={14} style={{ color: '#25D366' }} /> WhatsApp Business Number
          </label>
          <input value={settings.whatsapp_number || ''}
            onChange={e => setSettings({ ...settings, whatsapp_number: e.target.value })}
            placeholder="+675 1234 5678 (with country code)" data-testid="setting-whatsapp" />
          <p className="form-hint">Customers see this on the order WhatsApp button.</p>
        </div>
      </section>

      {/* Hero cover image */}
      <section className="settings-section" data-testid="cover-section">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <ImageIcon size={18} /> Hero Cover Image
        </h3>
        <p className="form-hint" style={{ marginTop: -4 }}>
          The image shown on the right of the navy hero on the homepage. Square is best (160×160px+).
        </p>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: 10 }}>
          <div style={{
            width: 140, height: 140, borderRadius: 16, overflow: 'hidden',
            background: 'var(--light-gray)', border: '1px solid var(--border-gray)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }} data-testid="cover-preview">
            {settings.hero_cover_image_url ? (
              <img src={settings.hero_cover_image_url} alt="Cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <ImageIcon size={36} style={{ color: 'var(--text-muted)' }} />
            )}
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Image URL</label>
              <input value={settings.hero_cover_image_url || ''}
                onChange={e => setSettings({ ...settings, hero_cover_image_url: e.target.value })}
                placeholder="https://.../cover.jpg or leave blank for placeholder"
                data-testid="cover-url-input" />
            </div>
            <label data-testid="cover-upload-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10,
                padding: '8px 14px', borderRadius: 10, background: 'var(--navy)', color: 'white',
                cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
              <Upload size={14} /> {uploadingCover ? 'Uploading…' : 'Upload from device'}
              <input type="file" accept="image/*" onChange={handleCoverUpload}
                disabled={uploadingCover} style={{ display: 'none' }} />
            </label>
          </div>
        </div>
      </section>

      {/* Promos editor */}
      <section className="settings-section" data-testid="promos-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
            <Sparkles size={18} /> Promo Carousel Slides
          </h3>
          <button onClick={addPromo} data-testid="add-promo-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '7px 12px', borderRadius: 8, border: '1px solid var(--navy)', background: 'var(--white)', color: 'var(--navy)', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700, fontFamily: 'inherit' }}>
            <Plus size={14} /> Add Slide
          </button>
        </div>
        <p className="form-hint" style={{ marginTop: -4 }}>
          Rotating slides at the top of the homepage. Set <code>sponsored_by</code> to monetize a slot (shows a "Sponsored" badge).
        </p>

        {settings.promos.length === 0 && (
          <div className="empty-state" data-testid="no-promos" style={{ padding: 20 }}>
            <p>No promo slides yet. Click "Add Slide" to create one.</p>
          </div>
        )}

        {settings.promos.map((p, idx) => (
          <div key={p.id} data-testid={`promo-row-${idx}`} className="promo-editor-card"
            style={{ background: `linear-gradient(135deg, ${p.gradient_from || '#0A1F44'}, ${p.gradient_to || '#1E40AF'})` }}>
            <div className="promo-editor-grid">
              <div className="form-group">
                <label style={{ color: 'white' }}>Title</label>
                <input value={p.title} onChange={e => updatePromo(idx, { title: e.target.value })} data-testid={`promo-title-${idx}`} />
              </div>
              <div className="form-group">
                <label style={{ color: 'white' }}>Subtitle</label>
                <input value={p.subtitle} onChange={e => updatePromo(idx, { subtitle: e.target.value })} data-testid={`promo-subtitle-${idx}`} />
              </div>
              <div className="form-group">
                <label style={{ color: 'white' }}>CTA Label</label>
                <input value={p.cta_label} onChange={e => updatePromo(idx, { cta_label: e.target.value })} data-testid={`promo-cta-${idx}`} />
              </div>
              <div className="form-group">
                <label style={{ color: 'white' }}>CTA Link</label>
                <input value={p.cta_link} onChange={e => updatePromo(idx, { cta_link: e.target.value })}
                  placeholder="/category/snacks  ·  whatsapp  ·  https://..."
                  data-testid={`promo-link-${idx}`} />
              </div>
              <div className="form-group">
                <label style={{ color: 'white' }}>Gradient From</label>
                <input type="color" value={p.gradient_from} onChange={e => updatePromo(idx, { gradient_from: e.target.value })} data-testid={`promo-from-${idx}`} style={{ height: 38, padding: 4 }} />
              </div>
              <div className="form-group">
                <label style={{ color: 'white' }}>Gradient To</label>
                <input type="color" value={p.gradient_to} onChange={e => updatePromo(idx, { gradient_to: e.target.value })} data-testid={`promo-to-${idx}`} style={{ height: 38, padding: 4 }} />
              </div>
              <div className="form-group">
                <label style={{ color: 'white' }}>Accent (CTA button colour)</label>
                <input type="color" value={p.accent} onChange={e => updatePromo(idx, { accent: e.target.value })} data-testid={`promo-accent-${idx}`} style={{ height: 38, padding: 4 }} />
              </div>
              <div className="form-group">
                <label style={{ color: 'white' }}>Sponsored by <span style={{ opacity: 0.8, fontWeight: 400, fontSize: '0.72rem' }}>(optional)</span></label>
                <input value={p.sponsored_by || ''} onChange={e => updatePromo(idx, { sponsored_by: e.target.value })}
                  placeholder="e.g. Eliseo Group" data-testid={`promo-sponsor-${idx}`} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, gap: 8, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'white', fontSize: '0.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={p.active !== false}
                  onChange={e => updatePromo(idx, { active: e.target.checked })}
                  data-testid={`promo-active-${idx}`}
                  style={{ width: 'auto', cursor: 'pointer' }} />
                Active (visible to customers)
              </label>
              <button onClick={() => removePromo(idx)} data-testid={`remove-promo-${idx}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.1)', color: 'white', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.82rem', fontWeight: 600 }}>
                <Trash2 size={13} /> Remove Slide
              </button>
            </div>
          </div>
        ))}
      </section>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
        <button className="btn-lime" onClick={save} disabled={saving} data-testid="save-all-btn-bottom"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: 'auto', padding: '10px 22px' }}>
          <Save size={16} /> {saving ? 'Saving…' : 'Save All'}
        </button>
      </div>
    </div>
  );
}
